
import { useEffect, useState, memo } from 'react';
import {
    Box, Button, Text, FormControl, FormLabel, Input, InputGroup,
    InputLeftElement, Flex, Heading, Icon,
} from '@chakra-ui/react';
import { Formik } from 'formik';
import { AiOutlineMail } from 'react-icons/ai';
import { useSelector } from 'react-redux';
import toast from 'react-hot-toast';
import * as Yup from 'yup';

import CustomSelect from 'components/CustomSelect';
import InputText from 'components/Input';
import SearchableSelect from 'components/SearchableSelect';
import { useCustomers, useCustomersMutations } from 'hooks/useCustomers';
import useDebounce from 'hooks/useDebounce';
import {
    useGetCitiesByStateId,
    useGetCountries,
    useGetStatesByCountryId,
} from 'hooks/useGetLocations';

/* ─────────────────────────────────────────────────────────────
   BRAND TOKENS
   ───────────────────────────────────────────────────────────── */
const NAVY        = '#1B3B5F';
const NAVY_DEEP   = '#0F2940';
// const CYAN        = '#DCF8F9';
const INK         = '#1A2740';
const MUTED       = '#5A6B7E';
const FAINT       = '#8896AE';
const SURFACE     = '#FFFFFF';
const SURFACE_ALT = '#F7F9FB';
const BORDER      = '#E8EDF5';
const DANGER      = '#E53E3E';

const FONT      = "'Bricolage Grotesque', sans-serif";
// const SHADOW_SM = '0 1px 3px rgba(27,59,95,0.06)';
/* ─────────────────────────────────────────────────────────────
   ICONS
   ───────────────────────────────────────────────────────────── */
const BackArrowIcon = (props) => (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" {...props}>
        <path d="M19 12H5M12 19l-7-7 7-7"
            stroke="currentColor" strokeWidth="2.2"
            strokeLinecap="round" strokeLinejoin="round" />
    </svg>
);

/* ─────────────────────────────────────────────────────────────
   VALIDATION
   ───────────────────────────────────────────────────────────── */
const emailRegExp = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/* Loose match — final formatting done in normalizePhone before payload */
const phoneRegExp = /^[0-9+\-\s()]{7,20}$/;

const checkoutValidationSchema = Yup.object().shape({
    first_name: Yup.string()
        .required('First name is required')
        .min(2, 'First name must be at least 2 characters')
        .max(50, 'First name must not exceed 50 characters'),
    last_name: Yup.string()
        .required('Last name is required')
        .min(2, 'Last name must be at least 2 characters')
        .max(50, 'Last name must not exceed 50 characters')
        .matches(/^[a-zA-Z\s'-]+$/, 'Last name can only contain letters'),
    phone: Yup.string()
        .required('Mobile number is required')
        .matches(phoneRegExp, 'Phone number is not valid')
        .test(
            'digits-only-length',
            'Phone number must be 10–14 digits',
            (val) => {
                if (!val) return false;
                const digits = val.replace(/\D/g, '');
                return digits.length >= 10 && digits.length <= 14;
            }
        ),
    email: Yup.string()
        .required('Email is required')
        .matches(emailRegExp, 'Email is not valid')
        .email('Invalid email format'),
    line1: Yup.string().required('Address line 1 is required'),
    /* Address fields only required when NOT using existing customer's saved address */
    country: Yup.mixed().when('useExistingAddress', {
        is: (val) => !val,
        then: () => Yup.object()
            .required('Country is required')
            .nullable(),
        otherwise: () => Yup.mixed().notRequired(),
    }),
    state: Yup.mixed().when('useExistingAddress', {
        is: (val) => !val,
        then: () => Yup.string()
            .required('State is required')
            .nullable(),
        otherwise: () => Yup.mixed().notRequired(),
    }),
    city: Yup.mixed().when('useExistingAddress', {
        is: (val) => !val,
        then: () => Yup.string()
            .required('City is required')
            .nullable(),
        otherwise: () => Yup.mixed().notRequired(),
    }),
    is_residential: Yup.boolean(),
    line2: Yup.string().optional(),
});

/* Normalize phone for backend — strip country code if user typed it,
   strip leading 0, return digits only. Country code is re-prepended at submit. */
const normalizePhone = (phone, countryCode = '') => {
    if (!phone) return '';
    let cleaned = phone.replace(/\D/g, '');
    if (countryCode && cleaned.startsWith(String(countryCode))) {
        cleaned = cleaned.slice(String(countryCode).length);
    }
    if (cleaned.startsWith('0')) {
        cleaned = cleaned.slice(1);
    }
    return cleaned;
};

/* ─────────────────────────────────────────────────────────────
   FORM FIELDS CONFIG
   ───────────────────────────────────────────────────────────── */
const FORM_FIELDS = [
    { label: 'First name',     type: 'text',   placeholder: 'Seun',                       name: 'first_name' },
    { label: 'Last name',      type: 'text',   placeholder: 'Akinloye',                   name: 'last_name'  },
    { label: 'Country',        type: 'select', placeholder: 'Select country',             name: 'country'    },
    { label: 'Email',          type: 'email',  placeholder: 'seunakinloye@gmail.com',     name: 'email'      },
    { label: 'Phone number',   type: 'tel',    placeholder: '90 9535 3542',               name: 'phone'      },
    { label: 'Address line 1', type: 'text',   placeholder: 'Lagos, Nigeria',             name: 'line1'      },
    { label: 'State',          type: 'select', placeholder: 'Select state',               name: 'state'      },
    { label: 'City',           type: 'select', placeholder: 'Select city',                name: 'city'       },
];

/* ─────────────────────────────────────────────────────────────
   SHARED PIECES
   ───────────────────────────────────────────────────────────── */

const FieldError = memo(function FieldError({ message }) {
    if (!message) return null;
    return (
        <Text
            mt="6px"
            fontSize="11px"
            color={DANGER}
            fontWeight="500"
            fontFamily={FONT}
            role="alert"
        >
            {message}
        </Text>
    );
});

const PrimaryButton = memo(function PrimaryButton({
    children, onClick, type, isDisabled, isLoading, fullWidth = true, ...rest
}) {
    return (
        <Button
            type={type ?? 'button'}
            onClick={onClick}
            isDisabled={isDisabled}
            isLoading={isLoading}
            bg={NAVY}
            color="white"
            _hover={{ bg: NAVY_DEEP }}
            _active={{ bg: NAVY_DEEP }}
            _disabled={{
                bg: BORDER,
                color: FAINT,
                cursor: 'not-allowed',
                _hover: { bg: BORDER },
            }}
            w={fullWidth ? 'full' : 'auto'}
            h="44px"
            borderRadius="10px"
            fontWeight="700"
            fontSize="13px"
            fontFamily={FONT}
            {...rest}
        >
            {children}
        </Button>
    );
});

const SecondaryButton = memo(function SecondaryButton({
    children, onClick, type, isDisabled, fullWidth = true, ...rest
}) {
    return (
        <Button
            type={type ?? 'button'}
            onClick={onClick}
            isDisabled={isDisabled}
            variant="outline"
            borderColor={BORDER}
            color={INK}
            _hover={{ borderColor: NAVY, bg: SURFACE_ALT }}
            _active={{ bg: SURFACE_ALT }}
            w={fullWidth ? 'full' : 'auto'}
            h="44px"
            borderRadius="10px"
            fontWeight="600"
            fontSize="13px"
            fontFamily={FONT}
            {...rest}
        >
            {children}
        </Button>
    );
});


/* ─────────────────────────────────────────────────────────────
   BACK BUTTON — appears at top of address / new views
   ───────────────────────────────────────────────────────────── */
const BackButton = memo(function BackButton({ onClick, label = 'Back' }) {
    return (
        <Button
            onClick={onClick}
            variant="ghost"
            leftIcon={<BackArrowIcon />}
            color={MUTED}
            fontWeight="700"
            fontSize="12px"
            fontFamily={FONT}
            h="32px"
            px="8px"
            ml="-8px"
            borderRadius="7px"
            _hover={{ bg: SURFACE_ALT, color: NAVY }}
            _focusVisible={{
                outline: `2px solid ${NAVY}`,
                outlineOffset: '2px',
            }}
            alignSelf="flex-start"
        >
            {label}
        </Button>
    );
});

/* ─────────────────────────────────────────────────────────────
   VIEW 1 — CHOOSE NEW vs EXISTING CUSTOMER
   ───────────────────────────────────────────────────────────── */

const ChooseCustomerView = ({
    chooseExisting, setChooseExisting,
    searchTerm, setSearchTerm,
    customers, isLoading,
    selectedCustomer, setSelectedCustomer,
    onContinueExisting, onChooseNew,
}) => (
    <Flex flexDir="column" gap="16px">
        <Box>
            <Heading
                as="h2"
                fontSize="16px"
                fontWeight="800"
                color={INK}
                fontFamily={FONT}
                letterSpacing="-0.01em"
                mb="4px"
            >
                How do you want to check out?
            </Heading>
            <Text fontSize="12px" color={MUTED}>
                Choose between an existing customer or a new one
            </Text>
        </Box>

        {/* Primary CTA — new customer (most common path) */}
        <PrimaryButton onClick={onChooseNew}>
            Checkout for a new customer
        </PrimaryButton>

        {/* Secondary CTA — existing customer */}
        <SecondaryButton onClick={() => setChooseExisting(!chooseExisting)}>
            {chooseExisting ? 'Cancel existing customer' : 'Checkout for an existing customer'}
        </SecondaryButton>

        {chooseExisting && (
            <Box
                mt="8px"
                p="14px"
                bg={SURFACE_ALT}
                border="1px solid"
                borderColor={BORDER}
                borderRadius="10px"
            >
                <Text
                    fontSize="8px"
                    color={FAINT}
                    fontWeight="700"
                    letterSpacing="0.08em"
                    mb="8px"
                >
                    SELECT CUSTOMER
                </Text>
                <SearchableSelect
                    name="customers"
                    searchTerm={searchTerm}
                    setSearchTerm={setSearchTerm}
                    // options={customers || []}
                    options={(customers || []).filter((c) => {
    const name = c?.contact_name ?? c?.company_name ?? '';
    const cleaned = String(name).trim();
    return cleaned && cleaned !== 'undefined undefined' && cleaned !== 'null null';
})}
                    loading={isLoading}
                    onSelect={(val) => setSelectedCustomer(val)}
                />
                <PrimaryButton
                    mt="12px"
                    onClick={onContinueExisting}
                    isDisabled={!selectedCustomer}
                >
                    Continue
                </PrimaryButton>
            </Box>
        )}
    </Flex>
);

/* ─────────────────────────────────────────────────────────────
   VIEW 2 — CHOOSE EXISTING OR NEW ADDRESS (existing customer)
   ───────────────────────────────────────────────────────────── */

const ChooseAddressView = ({
    customerName, hasValidAddress, onUseExisting, onUseNew, onBack
}) => (
    <Flex flexDir="column" gap="16px">
        <BackButton onClick={onBack} />
        <Box>
            <Heading
                as="h2"
                fontSize="16px"
                fontWeight="800"
                color={INK}
                fontFamily={FONT}
                letterSpacing="-0.01em"
                mb="4px"
            >
                Select address for {customerName}
            </Heading>
            <Text fontSize="12px" color={MUTED}>
                {hasValidAddress
                    ? 'Use the saved address or enter a new one'
                    : 'No saved address — please enter a new one'}
            </Text>
        </Box>

        <PrimaryButton
            onClick={onUseExisting}
            isDisabled={!hasValidAddress}
        >
            Use existing address
        </PrimaryButton>

        <SecondaryButton onClick={onUseNew}>
            Use new address
        </SecondaryButton>
    </Flex>
);

/* ─────────────────────────────────────────────────────────────
   VIEW 3 — FORM (address & contact entry)
   ───────────────────────────────────────────────────────────── */

const FormFieldRenderer = ({
    field, values, errors, touched,
    handleChange, handleBlur, setFieldValue,
    locations, setSelectedLocations,
}) => {
    const { type, label, placeholder, name } = field;
    const hasError = touched[name] && errors[name];

    if (type === 'text' || type === 'tel') {
        return (
            <FormControl mb="18px">
                <FormLabel
                    fontSize="12px"
                    fontWeight="700"
                    color={INK}
                    fontFamily={FONT}
                    mb="6px"
                >
                    {label}
                </FormLabel>
                <InputText
                    onBlur={handleBlur}
                    type={type}
                    placeholder={placeholder}
                    name={name}
                    prefix={
                        type === 'tel' && values?.country?.phonecode
                            ? `+${values.country.phonecode}`
                            : ''
                    }
                    value={values[name] || ''}
                    onChange={handleChange}
                />
                <FieldError message={hasError ? errors[name] : null} />
            </FormControl>
        );
    }

    if (type === 'select') {
        const options = locations[name]?.length > 0
            ? locations[name].map((item) => ({
                  label: item?.name,
                  value: item.isoCode,
                  phonecode: item.phonecode ?? undefined,
              }))
            : [];

        return (
            <FormControl mb="18px">
                <FormLabel
                    fontSize="12px"
                    fontWeight="700"
                    color={INK}
                    fontFamily={FONT}
                    mb="6px"
                >
                    {label}
                </FormLabel>
                <CustomSelect
                    placeholder={placeholder}
                    fontSize="13px"
                    name={name}
                    onChange={(e) => {
                        const selected = JSON.parse(e.target.value);
                        if (name === 'country') {
                            setFieldValue('country', selected);
                        } else {
                            setFieldValue(name, selected.label);
                        }
                        setSelectedLocations((prev) => ({
                            ...prev,
                            [name]: selected,
                        }));
                    }}
                    disabled={options.length === 0}
                    options={options}
                    value={
                        values[name]
                            ? JSON.stringify({
                                  label: values[name],
                                  value: values[name],
                                  phonecode: values[name]?.phonecode,
                              })
                            : ''
                    }
                />
                <FieldError message={hasError ? errors[name] : null} />
            </FormControl>
        );
    }

    if (type === 'email') {
        return (
            <FormControl mb="18px">
                <FormLabel
                    fontSize="12px"
                    fontWeight="700"
                    color={INK}
                    fontFamily={FONT}
                    mb="6px"
                >
                    {label}
                </FormLabel>
                <InputGroup>
                    <InputLeftElement pointerEvents="none" color={FAINT}>
                        <Icon as={AiOutlineMail} />
                    </InputLeftElement>
                    <Input
                        type={type}
                        placeholder={placeholder}
                        fontSize="13px"
                        fontFamily={FONT}
                        value={values[name] || ''}
                        name={name}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        h="42px"
                        bg={SURFACE}
                        border="1px solid"
                        borderColor={BORDER}
                        borderRadius="8px"
                        _focus={{
                            borderColor: NAVY,
                            boxShadow: `0 0 0 1px ${NAVY}`,
                        }}
                    />
                </InputGroup>
                <FieldError message={hasError ? errors[name] : null} />
            </FormControl>
        );
    }

    return null;
};

/* ─────────────────────────────────────────────────────────────
   MAIN — CHECKOUT FORM
   ───────────────────────────────────────────────────────────── */

export default function CheckoutForm({
    setTabIndex,
    setCheckoutPayload,
    checkoutPayload,
    isSample = false,
    tabIndex: currentStep,  /* rename to avoid DOM attribute collision */
}) {
    const verifyReducer = useSelector((state) => state.verifyReducer);
    const { verifiedUser } = verifyReducer;

    const [view, setView] = useState('main'); /* main | address | new */
    const [chooseExisting, setChooseExisting] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const debouncedSearch = useDebounce(searchTerm, 700);

    const [selectedCustomer, setSelectedCustomer] = useState(null);
    const [selectedLocations, setSelectedLocations] = useState({
        country: null,
        state: null,
        city: null,
    });

    /* Location dropdowns — cascading country → state → city */
    const countries = useGetCountries();
    const states = useGetStatesByCountryId(selectedLocations?.country?.value);
    const cities = useGetCitiesByStateId(
        selectedLocations?.state?.value,
        selectedLocations?.country?.value
    );
    const locations = {
        country: countries ?? [],
        state:   states ?? [],
        city:    cities ?? [],
    };

    const { fetchCustomers, customers, isLoading } = useCustomers();
    const { createCustomer, isPending } = useCustomersMutations();

    /* Scroll to top on mount */
    useEffect(() => {
        window.scrollTo({ top: 0, behavior: 'smooth' });
    }, []);

    /* Refetch customer suggestions when search term changes */
    useEffect(() => {
        fetchCustomers(debouncedSearch);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [debouncedSearch]);

    /* ── Address utilities for existing customer ────────────── */

    const getCustomerAddress = () => {
        if (!selectedCustomer) return {};

        const {
            cf_line_address, cf_city, cf_state, cf_country,
            first_name, last_name, email, phone, zip,
        } = selectedCustomer;

        const address = {
            first_name: first_name || '',
            last_name:  last_name || '',
            email:      email || '',
            phone:      phone || '',
            line1:      cf_line_address || '',
            state:      cf_state || '',
            city:       cf_city || '',
            country:    cf_country || '',
            line2:      '',
            is_residential: true,
            metadata: { customerId: verifiedUser?.data?.user?.id },
            useExistingAddress: true,
        };
        if (zip) address.zip = zip;
        return address;
    };

    const hasValidExistingAddress = Boolean(
        selectedCustomer &&
        selectedCustomer?.cf_line_address?.trim() &&
        selectedCustomer?.cf_city?.trim() &&
        selectedCustomer?.cf_state?.trim() &&
        selectedCustomer?.cf_country?.trim()
    );

    const handleUseExistingAddress = () => {
        if (!selectedCustomer) return;
        const addressPayload = getCustomerAddress();
        setCheckoutPayload((prev) => ({
            ...prev,
            address: addressPayload,
            customer_id: selectedCustomer.contact_id,
        }));
        setTabIndex(1);
    };

    const getInitialValues = (useExistingAddress = false) => {
        if (useExistingAddress) {
            return getCustomerAddress();
        }
        return {
            first_name: selectedCustomer?.first_name ?? '',
            last_name:  selectedCustomer?.last_name ?? '',
            email:      selectedCustomer?.email ?? '',
            phone:      selectedCustomer?.phone ?? '',
            is_residential: true,
            line1:    '',
            line2:    '',
            city:     '',
            state:    '',
            country:  '',
            zip:      '',
            metadata: { customerId: verifiedUser?.data?.user?.id },
            useExistingAddress: false,
        };
    };

    /* ── Render ─────────────────────────────────────────────── */

    if (view === 'main') {
        return (
            <ChooseCustomerView
                chooseExisting={chooseExisting}
                setChooseExisting={setChooseExisting}
                searchTerm={searchTerm}
                setSearchTerm={setSearchTerm}
                customers={customers}
                isLoading={isLoading}
                selectedCustomer={selectedCustomer}
                setSelectedCustomer={setSelectedCustomer}
                onContinueExisting={() => setView('address')}
                onChooseNew={() => setView('new')}
            />
        );
    }

    if (view === 'address') {
        return (
            <ChooseAddressView
                customerName={`${selectedCustomer?.first_name ?? ''} ${selectedCustomer?.last_name ?? ''}`.trim()}
                hasValidAddress={hasValidExistingAddress}
                onUseExisting={handleUseExistingAddress}
                onUseNew={() => {
                    setSelectedCustomer({
                        ...selectedCustomer,
                        line1: '', line2: '', city: '', state: '', country: '', zip: '',
                    });
                    setView('new');
                }}
                onBack={() => {
                    setView('main');
                    setSelectedCustomer(null);
                }}
            />
        );
    }

    /* view === 'new' — show form */
    return (
        <Formik
            enableReinitialize
            initialValues={getInitialValues(false)}
            validationSchema={checkoutValidationSchema}
            onSubmit={async (values, { setSubmitting }) => {
                setSubmitting(true);

                const phoneDigits = normalizePhone(values.phone, values.country?.phonecode);
                const formattedPhone = `+${values.country?.phonecode}${phoneDigits}`;

                const isSameAsExisting =
                    selectedCustomer ||
                    (checkoutPayload?.address?.email === values.email &&
                     checkoutPayload?.address?.phone === formattedPhone);

                if (isSameAsExisting) {
                    /* Returning customer — skip create, just update payload */
                    setCheckoutPayload((prev) => ({
                        ...prev,
                        address: {
                            ...values,
                            phone: formattedPhone,
                            country: values.country?.value,
                        },
                        customer_id:
                            selectedCustomer?.contact_id || prev?.customer_id,
                    }));
                    setTabIndex(1);
                } else {
                    /* New customer — create in backend, then proceed */
                    try {
                        const customerData = {
                            ...values,
                            phone: formattedPhone,
                            country: values.country?.value,
                        };
                        const resp = await createCustomer(customerData);
                        if (resp) {
                            setCheckoutPayload((prev) => ({
                                ...prev,
                                address: {
                                    ...values,
                                    phone: formattedPhone,
                                    country: values.country?.value,
                                },
                                customer_id:
                                    resp?.contact?.contact_id || resp?.zoho_id,
                            }));
                            setTabIndex(1);
                        } else {
                            toast.error('Could not create customer. Please try again.');
                        }
                    } catch (error) {
                        console.error('Error creating customer:', error);
                        toast.error(
                            error?.response?.data?.message ||
                            'Could not create customer. Please try again.'
                        );
                    }
                }

                setSubmitting(false);
            }}
        >
            {({
                values, errors, touched,
                handleChange, handleBlur, handleSubmit,
                isSubmitting, setFieldValue,
            }) => (
                <form onSubmit={handleSubmit}>
                    {/* Back button */}
                    <Box mb="14px">
                        <BackButton
                            onClick={() => {
                                /* Go back to main if we came from main,
                                   or back to address view if we came from existing-customer flow */
                                if (selectedCustomer) {
                                    setView('address');
                                } else {
                                    setView('main');
                                }
                            }}
                        />
                    </Box>

                    {/* Form section heading */}
                    <Box mb="20px">
                        <Heading
                            as="h2"
                            fontSize="16px"
                            fontWeight="800"
                            color={INK}
                            fontFamily={FONT}
                            letterSpacing="-0.01em"
                            mb="4px"
                        >
                            Customer details
                        </Heading>
                        <Text fontSize="12px" color={MUTED}>
                            Fill in the shipping and contact information
                        </Text>
                    </Box>

                    {FORM_FIELDS.map((field) => (
                        <FormFieldRenderer
                            key={field.name}
                            field={field}
                            values={values}
                            errors={errors}
                            touched={touched}
                            handleChange={handleChange}
                            handleBlur={handleBlur}
                            setFieldValue={setFieldValue}
                            locations={locations}
                            setSelectedLocations={setSelectedLocations}
                        />
                    ))}

                    {((isSample && currentStep === 0) || !isSample) && (
                        <Box mt="8px">
                            <PrimaryButton
                                type={isSample ? 'button' : 'submit'}
                                onClick={isSample ? handleSubmit : undefined}
                                isLoading={isSubmitting || isPending}
                            >
                                Continue
                            </PrimaryButton>
                        </Box>
                    )}
                </form>
            )}
        </Formik>
    );
}